import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import type { NoteType } from "@/types/board";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import CodeBlock from '@tiptap/extension-code-block';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import {
  Bold,
  Italic,
  Code,
  List,
  ListOrdered,
  Link as LinkIcon,
  CheckSquare,
  Quote,
  Heading2,
} from 'lucide-react';

interface EnhancedNoteEditorProps {
  value: string;
  onChange: (value: string) => void;
  noteType?: NoteType;
  onNoteTypeChange?: (type: NoteType) => void;
}

const meetingTemplate = `<h2>Meeting Notes</h2>
<p><strong>Attendees:</strong></p>
<ul>
  <li></li>
</ul>
<p><strong>Agenda:</strong></p>
<ol>
  <li></li>
</ol>
<p><strong>Discussion:</strong></p>
<ul>
  <li></li>
</ul>
<p><strong>Action Items:</strong></p>
<ul class="task-list">
  <li data-type="taskItem" data-checked="false"></li>
</ul>`;

const progressTemplate = `<h2>Progress Update</h2>
<p><strong>Status:</strong> [On Track/At Risk/Blocked]</p>
<p><strong>Progress Made:</strong></p>
<ul>
  <li></li>
</ul>
<p><strong>Next Steps:</strong></p>
<ul>
  <li></li>
</ul>
<p><strong>Blockers:</strong></p>
<ul>
  <li></li>
</ul>`;

const blockerTemplate = `<h2>Blocker/Issue</h2>
<p><strong>Issue Description:</strong></p>
<p></p>
<p><strong>Impact:</strong></p>
<p></p>
<p><strong>Possible Solutions:</strong></p>
<ol>
  <li></li>
</ol>
<p><strong>Required Support:</strong></p>
<ul>
  <li></li>
</ul>`;

const actionTemplate = `<h2>Action Items</h2>
<ul class="task-list">
  <li data-type="taskItem" data-checked="false">Task: </li>
</ul>
<p><strong>Due Date:</strong></p>
<p></p>
<p><strong>Dependencies:</strong></p>
<ul>
  <li></li>
</ul>
<p><strong>Notes:</strong></p>
<ul>
  <li></li>
</ul>`;

const noteTemplates: Record<NoteType, string> = {
  general: "",
  meeting: meetingTemplate,
  progress: progressTemplate,
  blocker: blockerTemplate,
  action: actionTemplate,
};

export function EnhancedNoteEditor({
  value,
  onChange,
  noteType = "general",
  onNoteTypeChange
}: EnhancedNoteEditorProps) {
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit,
      CodeBlock,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 hover:underline cursor-pointer',
        },
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const handleTypeChange = (type: NoteType) => {
    if (onNoteTypeChange) {
      onNoteTypeChange(type);
    }
    // Only set template if the note is empty
    if (editor && !editor.getText().trim()) {
      editor.commands.setContent(noteTemplates[type]);
    }
  };

  const handleLinkSubmit = () => {
    if (editor) {
      if (linkUrl === '') {
        editor.chain().focus().unsetLink().run();
      } else {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
    }
    setIsLinkDialogOpen(false);
    setLinkUrl('');
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <div className="space-y-2 mb-4">
        <Label>Note Type</Label>
        <Select value={noteType} onValueChange={(val) => handleTypeChange(val as NoteType)}>
          <SelectTrigger>
            <SelectValue placeholder="Select note type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General Note</SelectItem>
            <SelectItem value="meeting">Meeting Notes</SelectItem>
            <SelectItem value="progress">Progress Update</SelectItem>
            <SelectItem value="blocker">Blocker</SelectItem>
            <SelectItem value="action">Action Items</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg flex flex-col">
        <div className="flex flex-wrap gap-1 p-2 border-b bg-gray-50">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className={editor?.isActive('bold') ? 'bg-gray-200' : ''}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className={editor?.isActive('italic') ? 'bg-gray-200' : ''}
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}
          >
            <Heading2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className={editor?.isActive('bulletList') ? 'bg-gray-200' : ''}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            className={editor?.isActive('orderedList') ? 'bg-gray-200' : ''}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleTaskList().run()}
            className={editor?.isActive('taskList') ? 'bg-gray-200' : ''}
          >
            <CheckSquare className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
            className={editor?.isActive('codeBlock') ? 'bg-gray-200' : ''}
          >
            <Code className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            className={editor?.isActive('blockquote') ? 'bg-gray-200' : ''}
          >
            <Quote className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsLinkDialogOpen(true)}
            className={editor?.isActive('link') ? 'bg-gray-200' : ''}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </div>
        <div
          className="p-2 overflow-y-auto flex-1 min-h-[200px] cursor-text"
          onClick={() => editor?.chain().focus().run()}
        >
          <EditorContent
            editor={editor}
            className="prose prose-sm max-w-none h-full"
          />
        </div>
      </div>

      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                placeholder="Enter URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLinkDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleLinkSubmit}>Add Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}